/**
 * Page Roles Configuration — 3-Zone Model
 *
 * Central mapping for:
 *   - Which admin sections each role can see
 *   - Which routes each role can access
 *   - Default landing page per role
 *
 * CHANGE LOG (rebuild):
 *   - ROLE_HOME: superintendent → /district/superintendent (was /district/workspace)
 *   - ROLE_HOME: cio → /district/cio (was /district/workspace)
 *   - ROLE_HOME: counselor → /district/counselor (was /district/workspace)
 *   - Teacher and district (admin) unchanged
 */

// ============================================================================
// ADMIN SECTIONS → ALLOWED ROLES
// ============================================================================

export const SECTION_ROLES = {
    overview:          ['district', 'admin', 'superintendent', 'cio'],
    superintendent:    ['district', 'admin', 'superintendent'],
    // 'analytics' was absent here, so isSectionVisible() defaulted it to
    // visible for EVERY role — a counselor (student-services only) saw the
    // Analytics section too. Gate it to the strategic roles.
    analytics:         ['district', 'admin', 'superintendent', 'cio'],
    cio:               ['district', 'admin', 'cio'],
    // 'district_scholarship_admin' — a scoped student-services district role
    // (reserved for future use). It sees the student-services and compliance
    // sections (which carry Opportunities, PartnerNetwork, and Consent); every
    // strategic section (overview/superintendent/cio/operations) stays hidden.
    // Route access is narrowed to exactly those three surfaces in ROUTE_ROLES
    // below.
    'student-services':['district', 'admin', 'counselor', 'principal', 'district_scholarship_admin'],
    operations:        ['district', 'admin', 'cio'],
    compliance:        ['district', 'admin', 'superintendent', 'district_scholarship_admin'],
};

// ============================================================================
// ROLE → HOME PAGE (landing after login / role switch)
// ============================================================================
// Each role lands on their most important dashboard.
// The workspace is still accessible via sidebar navigation.

export const ROLE_HOME = {
    teacher:          '/district/workspace',
    principal:        '/district/workspace',
    counselor:        '/district/counselor',
    superintendent:   '/district/superintendent',
    cio:              '/district/cio',
    district:         '/district',
    admin:            '/district',         // full district admin lands on overview
    // Scoped student-services role lands directly on the scholarships it
    // administers, never the strategic overview.
    district_scholarship_admin: '/district/opportunities',
};

// ============================================================================
// ROLES THAT SEE THE ADMINISTRATION ZONE
// ============================================================================

export const ADMIN_ZONE_ROLES = ['district', 'admin', 'superintendent', 'cio', 'counselor', 'principal', 'district_scholarship_admin'];

// ============================================================================
// ROUTE → ALLOWED ROLES
// ============================================================================

export const ROUTE_ROLES = {
    // Overview
    '/district':                    ['district', 'superintendent', 'cio'],

    // Superintendent section
    '/district/superintendent':     ['district', 'superintendent'],
    '/district/program-report':     ['district', 'superintendent', 'admin'],
    '/district/value':              ['district', 'superintendent'],
    '/district/initiatives':        ['district', 'superintendent'],
    '/district/investment-matrix':  ['district', 'superintendent'],
    '/district/budget-scenarios':   ['district', 'superintendent'],
    '/district/analytics':          ['district', 'superintendent'],

    // Cascade Implementation System
    '/district/cascade':            ['district', 'superintendent'],

    // CIO / Technology
    '/district/cio':                ['district', 'cio'],
    '/district/endpoint-agents':    ['district', 'cio'],
    '/district/agents':             ['district', 'cio'],
    '/district/integrations':       ['district', 'cio'],

    // Partner Network
    // 'district_scholarship_admin' (scoped student-services role) is limited to
    // exactly these two student-services surfaces plus Consent below — the three
    // a scholarship-administering district user needs, and nothing strategic.
    '/district/partners':           ['district', 'cio', 'superintendent', 'counselor', 'district_scholarship_admin'],
    '/district/opportunities':      ['district', 'cio', 'superintendent', 'counselor', 'district_scholarship_admin'],
    // Sprint 4 PR-4d — Partner Contracts. Read allowlist matches the route's
    // READ_ALLOWLIST_ROLES (fiscal set + counselor for D-F). Write controls
    // are gated server-side by routes/district/contracts.js's
    // requireRoleIn(FISCAL_AUTHORITY_ROLES); the UI hides them as UX.
    '/district/contracts':          ['district', 'cio', 'superintendent', 'counselor'],

    // Student Services
    '/district/counselor':          ['district', 'counselor', 'principal'],
    '/district/escalations':        ['district', 'counselor', 'principal'],
    '/district/benefits':           ['district', 'counselor', 'principal'],

    // Operations
    '/district/workflows':          ['district', 'cio'],
    '/district/users':              ['district', 'cio'],
    '/district/calendar':           ['district', 'cio'],
    '/district/knowledge':          ['district', 'cio'],
    '/workspace/roles':             ['district', 'cio'],

    // Compliance
    '/district/consent':            ['district', 'superintendent', 'district_scholarship_admin'],
    '/district/audit':              ['district', 'superintendent'],
    '/district/billing':            ['district', 'superintendent'],
    '/district/settings':           ['district'],

    // Workspace & Growth — accessible to ALL district roles
    '/district/workspace':          ['district', 'superintendent', 'cio', 'counselor', 'principal', 'teacher'],
    '/district/growth':             ['district', 'superintendent', 'cio', 'counselor', 'principal', 'teacher'],
};

/**
 * Check if a role is allowed to access a given route.
 */
export function isRouteAllowed(role, route) {
    // admin and district roles have unrestricted district portal access
    if (role === 'admin' || role === 'district') return true;
    if (ROUTE_ROLES[route]) {
        return ROUTE_ROLES[route].includes(role);
    }
    const prefixes = Object.keys(ROUTE_ROLES);
    for (const prefix of prefixes) {
        if (route.startsWith(prefix + '/') && ROUTE_ROLES[prefix]) {
            return ROUTE_ROLES[prefix].includes(role);
        }
    }
    return true;
}

/**
 * Check if a role can see a given admin sidebar section.
 */
export function isSectionVisible(role, sectionId) {
    if (!SECTION_ROLES[sectionId]) return true;
    return SECTION_ROLES[sectionId].includes(role);
}

/**
 * Get the home page for a role.
 */
export function getHomeForRole(role) {
    // Default to the district overview — NEVER the Canvas workspace. A role-less
    // or unmapped session (e.g. a cross-portal/odd session) must not be silently
    // dumped into /district/workspace, which bounces to the authenticated Canvas
    // SPA. Unknown roles land on /district (itself guarded), not the workspace.
    return ROLE_HOME[role] || '/district';
}
